import { useEffect, useState } from 'react';

import { useDebouncedValue } from '@mantine/hooks';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { RegistryOrderQueueRow } from '@/types/registry-documents';
import { toError } from '@/utils/errors';

/** Keystrokes are grouped before a search goes out. */
export const REGISTRY_ORDER_SEARCH_DEBOUNCE_MS = 250;

/** Enough for a picker: a search that matches more is narrowed further. */
export const REGISTRY_ORDER_SEARCH_PAGE_SIZE = 20;

/** The rows a search returned, and the search they answer. */
interface Found {
  q: string;
  rows: RegistryOrderQueueRow[];
  error: Error | null;
}

const NOTHING: Found = { q: '', rows: [], error: null };

/**
 * Orders matching a company name or GTI reference, for a picker: the
 * admin queue's server-side search under the `all` view, read as the
 * person types.
 *
 * Keystrokes are debounced; a search still in flight is aborted when the
 * text changes, so a slow answer to an earlier search never lands over a
 * later one. The last answer stays in `rows` while the next one is pending
 * (`isLoading`), so a picker does not blank on every keystroke. An empty
 * search sends nothing and answers no rows at once. `enabled` false does
 * the same, so a closed dialog searches nothing.
 */
export function useRegistryOrderSearch(q: string, enabled: boolean) {
  const wanted = enabled ? q.trim() : '';
  const [debounced] = useDebouncedValue(wanted, REGISTRY_ORDER_SEARCH_DEBOUNCE_MS);
  const [found, setFound] = useState<Found>(NOTHING);
  // The debounce keeps the previous text while the new one settles; an
  // empty search must not wait on that.
  const term = wanted === '' ? '' : debounced;

  useEffect(() => {
    if (term === '') {
      setFound(NOTHING);
      return;
    }
    const controller = new AbortController();
    vnextApiClient
      .listRegistryOrderQueue(
        { view: 'all', q: term, page: 1, pageSize: REGISTRY_ORDER_SEARCH_PAGE_SIZE },
        { signal: controller.signal }
      )
      .then((result) => {
        if (!controller.signal.aborted) {
          setFound({
            q: term,
            rows: result === 'not-modified' ? [] : result.data.items,
            error: null,
          });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        const error = toError(err, 'Search failed');
        Sentry.captureException(error);
        setFound({ q: term, rows: [], error });
      });
    return () => controller.abort();
  }, [term]);

  const settled = wanted === '' || found.q === wanted;
  return {
    /** The last answer, kept while the next search settles; nothing for an
     * empty search. */
    rows: wanted === '' ? [] : found.rows,
    /** A search is pending or in flight for the text as it is now. */
    isLoading: !settled,
    /** Only an answer to the text as it is now: a failed earlier search is
     * not held against the next one. */
    error: settled && wanted !== '' ? found.error : null,
  };
}
